/**
 * ProjectVisas — onglet « Visas » du chantier (RP52 → RP54, revue produit 2026-09 ; D22).
 *
 * - Tableau de suivi par lot : À viser, VSO, VAO, Refusé, en retard (délai de 15 jours,
 *   CCAG-Travaux art. 29).
 * - Liste des visas (le serveur filtre : l'entreprise ne voit que les siens, le
 *   partenaire ceux de ses lots et ceux qu'il vise).
 * - Fiche visa : document, historique des indices ; avis VSO / VAO / Refusé avec
 *   observations et PDF annoté (viseur seulement) ; nouvel indice (entreprise, après
 *   VAO ou Refusé) ; changement de viseur (architecte, chef de projet).
 * - Entreprise : dépôt d'un document d'exécution à viser.
 * - Architecte : lots rattachés à chaque partenaire (le viseur par défaut d'un lot).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Stamp, Warning, DownloadSimple, ClockCounterClockwise, UsersThree, Spinner } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Skeleton } from '../../ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import usePermissions from '../../../hooks/usePermissions';
import DepotPiece from './DepotPiece';
import {
  STATUT_VISA, STATUT_VISA_COURT, STATUT_VISA_COULEUR, libellePiece, libelleDelai, dateFr,
} from './visasLibelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOUS = '__tous__';

function BadgeStatut({ statut, court = false }) {
  return (
    <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${STATUT_VISA_COULEUR[statut] || 'bg-gray-100 text-gray-700'}`}>
      {(court ? STATUT_VISA_COURT : STATUT_VISA)[statut] || 'À viser'}
    </span>
  );
}

function lienFichier(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
}

export default function ProjectVisas({ chantierId }) {
  const { role_projet: roleProjet } = usePermissions(chantierId);
  const [searchParams] = useSearchParams();
  const [visas, setVisas] = useState([]);
  const [suivi, setSuivi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState(TOUS);
  const [filtreLot, setFiltreLot] = useState(TOUS);
  const [ouvert, setOuvert] = useState(searchParams.get('visa'));

  const estPilote = ['archi', 'moe_principal'].includes(roleProjet);
  const estEntreprise = roleProjet === 'entreprise';

  const charger = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/visas`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/visas/suivi`, { withCredentials: true }),
      ]);
      setVisas(l.data || []);
      setSuivi(s.data || null);
    } catch {
      setVisas([]);
      setSuivi(null);
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger]);

  const visibles = visas.filter((v) => (filtreStatut === TOUS || v.statut === filtreStatut)
    && (filtreLot === TOUS || (v.lot_id || '_sans_lot') === filtreLot));

  return (
    <div className="space-y-6" data-testid="project-visas">
      <div className="flex items-center gap-2">
        <Stamp size={22} className="text-[#1E3A5F]" />
        <h2 className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Visas des documents d'exécution</h2>
      </div>

      {estEntreprise && <DepotPiece chantierId={chantierId} roleProjet={roleProjet} onDeposited={charger} compact />}

      {/* Tableau de suivi (RP54) */}
      {loading ? <Skeleton className="h-32" /> : suivi && suivi.totaux.total > 0 && (
        <div className="bg-white border border-[#E0E0DB] overflow-x-auto" data-testid="visas-suivi">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAF8] text-xs text-[#6B6B6B] uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Lot</th>
                {['a_viser', 'vso', 'vao', 'refuse'].map((s) => <th key={s} className="px-3 py-2 text-center">{STATUT_VISA_COURT[s]}</th>)}
                <th className="px-3 py-2 text-center">En retard</th>
                <th className="px-3 py-2 text-left">Prochaine date limite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0ED]">
              {suivi.par_lot.map((row) => (
                <tr
                  key={row.lot_id || '_sans_lot'}
                  className="hover:bg-[#FAFAF8] cursor-pointer"
                  onClick={() => setFiltreLot(row.lot_id || '_sans_lot')}
                  data-testid={`visas-suivi-lot-${row.lot_code || 'sans'}`}
                >
                  <td className="px-3 py-2 font-medium text-[#2C2C2C]">{row.lot_code ? `${row.lot_code} — ${row.lot_nom}` : row.lot_nom}</td>
                  <td className="px-3 py-2 text-center">{row.a_viser || '·'}</td>
                  <td className="px-3 py-2 text-center">{row.vso || '·'}</td>
                  <td className="px-3 py-2 text-center">{row.vao || '·'}</td>
                  <td className="px-3 py-2 text-center">{row.refuse || '·'}</td>
                  <td className={`px-3 py-2 text-center ${row.en_retard ? 'text-rose-700 font-semibold' : ''}`}>{row.en_retard || '·'}</td>
                  <td className="px-3 py-2 text-[#6B6B6B]">{dateFr(row.prochaine_limite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <Select value={filtreStatut} onValueChange={setFiltreStatut}>
          <SelectTrigger className="w-56 rounded-none" data-testid="visas-filtre-statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>Tous les statuts</SelectItem>
            {Object.entries(STATUT_VISA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtreLot !== TOUS && (
          <Button variant="outline" className="rounded-none text-sm" onClick={() => setFiltreLot(TOUS)}>Tous les lots</Button>
        )}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : visibles.length === 0 ? (
        <div className="bg-white border border-[#E0E0DB] text-center py-12 text-[#6B6B6B]" data-testid="visas-vide">
          {estEntreprise ? "Aucun document déposé pour visa. Déposez vos plans d'exécution ci-dessus." : 'Aucun visa pour le moment.'}
        </div>
      ) : (
        <div className="bg-white border border-[#E0E0DB] divide-y divide-[#F0F0ED]">
          {visibles.map((v) => (
            <button
              key={v.id}
              type="button"
              className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 hover:bg-[#FAFAF8]"
              onClick={() => setOuvert(v.id)}
              data-testid={`visa-ligne-${v.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C] truncate">{v.titre} <span className="text-[#6B6B6B] font-normal">— {libellePiece(v.nature, v.indice)}</span></p>
                <p className="text-xs text-[#9A9A9A]">
                  {v.lot_code ? `${v.lot_code} · ` : ''}{v.entreprise_nom || v.deposant_nom} · Viseur : {v.viseur_nom || '—'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <BadgeStatut statut={v.statut} court />
                {v.statut === 'a_viser' && (
                  <span className={`text-xs ${v.en_retard ? 'text-rose-700 font-semibold' : 'text-[#6B6B6B]'}`}>
                    {v.en_retard && <Warning size={12} className="inline mr-1" />}
                    Avant le {dateFr(v.date_limite)} · {libelleDelai(v)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {estPilote && <PartenairesLots chantierId={chantierId} />}

      {ouvert && (
        <FicheVisa
          visaId={ouvert}
          chantierId={chantierId}
          onClose={() => setOuvert(null)}
          onChange={charger}
        />
      )}
    </div>
  );
}

// ==================== Fiche visa ====================

function FicheVisa({ visaId, chantierId, onClose, onChange }) {
  const [v, setV] = useState(null);
  const [viseurs, setViseurs] = useState([]);
  const [avis, setAvis] = useState('vso');
  const [commentaire, setCommentaire] = useState('');
  const [pdf, setPdf] = useState(null);
  const [fichierIndice, setFichierIndice] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/visas/${visaId}`, { withCredentials: true });
      setV(data);
      if (data.peut_changer_viseur) {
        const r = await axios.get(`${API_URL}/api/chantiers/${chantierId}/visas/viseurs`, { withCredentials: true });
        setViseurs(r.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Visa introuvable');
      onClose();
    }
  }, [visaId, chantierId, onClose]);

  useEffect(() => { charger(); }, [charger]);

  const apres = async () => { await charger(); onChange(); };

  const rendreAvis = async () => {
    const fd = new FormData();
    fd.append('statut', avis);
    fd.append('commentaire', commentaire);
    if (pdf) fd.append('file', pdf);
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/visas/${visaId}/avis`, fd, { withCredentials: true });
      toast.success(`Avis rendu : ${STATUT_VISA_COURT[avis]}`);
      setCommentaire(''); setPdf(null);
      await apres();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer l'avis");
    } finally { setEnvoi(false); }
  };

  const deposerIndice = async () => {
    if (!fichierIndice) { toast.error('Choisissez le fichier du nouvel indice'); return; }
    const fd = new FormData();
    fd.append('file', fichierIndice);
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/visas/${visaId}/indice`, fd, { withCredentials: true });
      toast.success(`Nouvel indice déposé : ${libellePiece(data.nature, data.indice)}`);
      setFichierIndice(null);
      await apres();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dépôt impossible');
    } finally { setEnvoi(false); }
  };

  const changerViseur = async (uid) => {
    try {
      await axios.put(`${API_URL}/api/visas/${visaId}/viseur`, { viseur_user_id: uid }, { withCredentials: true });
      toast.success('Viseur modifié');
      await apres();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Modification impossible');
    }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:w-[520px] overflow-y-auto" data-testid="fiche-visa">
        {!v ? <Skeleton className="h-64 mt-8" /> : (
          <>
            <SheetHeader>
              <SheetTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{v.titre}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <BadgeStatut statut={v.statut} />
                <span className="text-[#6B6B6B]">{libellePiece(v.nature, v.indice)}</span>
              </div>
              <dl className="grid grid-cols-3 gap-y-1 text-xs">
                <dt className="text-[#9A9A9A]">Lot</dt><dd className="col-span-2">{v.lot_code ? `${v.lot_code} — ${v.lot_nom}` : 'Sans lot'}</dd>
                <dt className="text-[#9A9A9A]">Déposé par</dt><dd className="col-span-2">{v.entreprise_nom || v.deposant_nom} · le {dateFr(v.date_depot)}</dd>
                <dt className="text-[#9A9A9A]">Viseur</dt><dd className="col-span-2">{v.viseur_nom || '—'}</dd>
                <dt className="text-[#9A9A9A]">Date limite</dt>
                <dd className={`col-span-2 ${v.en_retard ? 'text-rose-700 font-semibold' : ''}`}>{dateFr(v.date_limite)} {libelleDelai(v) && `· ${libelleDelai(v)}`}</dd>
              </dl>
              {v.document_url && (
                <a href={lienFichier(v.document_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1E3A5F] underline" data-testid="visa-document">
                  <DownloadSimple size={14} /> {v.document_nom || 'Document'}
                </a>
              )}

              {v.statut !== 'a_viser' && (
                <div className="bg-[#FAFAF8] border-l-4 border-[#1E3A5F] p-3" data-testid="visa-avis">
                  <p className="font-semibold">Avis : {STATUT_VISA[v.statut]}</p>
                  {v.commentaire && <p className="mt-1 whitespace-pre-wrap">{v.commentaire}</p>}
                  <p className="text-xs text-[#9A9A9A] mt-1">{v.avis_par_nom} · {dateFr(v.avis_at)}</p>
                  {v.pdf_annote_url && (
                    <a href={lienFichier(v.pdf_annote_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1E3A5F] underline text-xs mt-1">
                      <DownloadSimple size={12} /> PDF annoté
                    </a>
                  )}
                </div>
              )}

              {v.peut_rendre_avis && (
                <div className="border border-[#E0E0DB] p-3 space-y-3" data-testid="visa-form-avis">
                  <p className="font-semibold">Rendre l'avis</p>
                  <div className="flex flex-col gap-1">
                    {['vso', 'vao', 'refuse'].map((s) => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="avis" checked={avis === s} onChange={() => setAvis(s)} className="accent-[#1E3A5F]" />
                        {STATUT_VISA[s]}
                      </label>
                    ))}
                  </div>
                  <div>
                    <Label className="text-xs">Observations {avis !== 'vso' && '(obligatoires)'}</Label>
                    <Textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={4} className="rounded-none" data-testid="visa-commentaire" />
                  </div>
                  <div>
                    <Label className="text-xs">PDF annoté (facultatif)</Label>
                    <Input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} className="rounded-none" />
                  </div>
                  <Button onClick={rendreAvis} disabled={envoi || (avis !== 'vso' && !commentaire.trim())} className="bg-[#1E3A5F] hover:bg-[#2C4A6F] rounded-none" data-testid="visa-envoyer-avis">
                    {envoi && <Spinner size={14} className="animate-spin mr-1" />}Enregistrer l'avis
                  </Button>
                </div>
              )}

              {v.peut_deposer_indice && (
                <div className="border border-[#E0E0DB] p-3 space-y-3" data-testid="visa-form-indice">
                  <p className="font-semibold">Nouvel indice</p>
                  <p className="text-xs text-[#6B6B6B]">Déposez le document corrigé : il repart pour visa avec un nouveau délai de 15 jours.</p>
                  <Input type="file" onChange={(e) => setFichierIndice(e.target.files?.[0] || null)} className="rounded-none" />
                  <Button onClick={deposerIndice} disabled={envoi || !fichierIndice} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Déposer le nouvel indice</Button>
                </div>
              )}

              {v.peut_changer_viseur && v.statut === 'a_viser' && viseurs.length > 0 && (
                <div>
                  <Label className="text-xs">Changer de viseur</Label>
                  <Select value={v.viseur_user_id || ''} onValueChange={changerViseur}>
                    <SelectTrigger className="rounded-none" data-testid="visa-changer-viseur"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      {viseurs.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name} — {p.libelle}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {v.historique?.length > 0 && (
                <div data-testid="visa-historique">
                  <p className="font-semibold flex items-center gap-1 mb-2"><ClockCounterClockwise size={14} /> Historique des indices</p>
                  <ul className="space-y-2">
                    {[...v.historique].reverse().map((h) => (
                      <li key={`${h.indice}-${h.date_depot}`} className="border-l-2 border-[#E0E0DB] pl-3 text-xs">
                        <p className="font-medium">{libellePiece(v.nature, h.indice)} · <BadgeStatut statut={h.statut} court /></p>
                        {h.commentaire && <p className="text-[#6B6B6B] whitespace-pre-wrap">{h.commentaire}</p>}
                        <p className="text-[#9A9A9A]">Déposé le {dateFr(h.date_depot)} · avis du {dateFr(h.avis_at)} ({h.avis_par_nom})</p>
                        <div className="flex gap-3">
                          {h.document_url && <a href={lienFichier(h.document_url)} target="_blank" rel="noreferrer" className="text-[#1E3A5F] underline">Document</a>}
                          {h.pdf_annote_url && <a href={lienFichier(h.pdf_annote_url)} target="_blank" rel="noreferrer" className="text-[#1E3A5F] underline">PDF annoté</a>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ==================== Partenaires et lots (D6) ====================

function PartenairesLots({ chantierId }) {
  const [partenaires, setPartenaires] = useState([]);
  const [lots, setLots] = useState([]);

  const charger = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/partenaires`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }),
      ]);
      setPartenaires(p.data || []);
      setLots(l.data || []);
    } catch { /* écran facultatif */ }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger]);

  const basculer = async (p, lotId) => {
    const actuels = new Set(p.lots || []);
    if (actuels.has(lotId)) actuels.delete(lotId); else actuels.add(lotId);
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/partenaires/${p.id}/lots`, { lots: [...actuels] }, { withCredentials: true });
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Modification impossible');
    }
  };

  if (!partenaires.length || !lots.length) return null;
  return (
    <div className="bg-white border border-[#E0E0DB] p-4" data-testid="visas-partenaires-lots">
      <p className="text-sm font-semibold text-[#2C2C2C] flex items-center gap-2 mb-1"><UsersThree size={16} /> Partenaires et lots</p>
      <p className="text-xs text-[#6B6B6B] mb-3">Le partenaire rattaché à un lot vise par défaut les documents d'exécution de ce lot. Sans partenaire, l'architecte vise.</p>
      <div className="space-y-3">
        {partenaires.map((p) => (
          <div key={p.id}>
            <p className="text-sm">{p.name}{p.specialite ? ` — ${p.specialite}` : ''}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {lots.map((l) => {
                const actif = (p.lots || []).includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => basculer(p, l.id)}
                    className={`text-xs px-2 py-1 border ${actif ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'border-[#E0E0DB] text-[#6B6B6B] hover:border-[#1E3A5F]'}`}
                    data-testid={`partenaire-${p.id}-lot-${l.code}`}
                  >
                    {l.code}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
